import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { Visualizer } from 'react-sound-visualizer';
import { useAuth } from '../../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../config/colors';
import { apiClient } from '../../../services/apiClient';

const EBML_MAGIC = [0x1a, 0x45, 0xdf, 0xa3];
const CLUSTER_ID = [0x1f, 0x43, 0xb6, 0x75];

/** Init segment from the last valid WebM in this page session. */
let cachedWebmInitSegment = null;

function startsWithBytes(bytes, signature) {
  if (bytes.length < signature.length) return false;
  return signature.every((byte, index) => bytes[index] === byte);
}

function indexOfBytes(bytes, signature) {
  const last = bytes.length - signature.length;
  for (let i = 0; i <= last; i += 1) {
    let found = true;
    for (let j = 0; j < signature.length; j += 1) {
      if (bytes[i + j] !== signature[j]) {
        found = false;
        break;
      }
    }
    if (found) return i;
  }
  return -1;
}

/**
 * MediaRecorder sometimes omits the EBML header on later takes (file starts at a Cluster).
 * Prepend the header captured from an earlier valid recording in this session.
 * @returns {{ blob: Blob, headerStatus: 'present' | 'repaired' | 'missing' }}
 */
async function repairWebmHeader(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const type = blob.type || 'audio/webm';

  if (startsWithBytes(bytes, EBML_MAGIC)) {
    const clusterAt = indexOfBytes(bytes, CLUSTER_ID);
    if (clusterAt > 0) {
      cachedWebmInitSegment = bytes.slice(0, clusterAt);
    }
    return { blob: new Blob([bytes], { type }), headerStatus: 'present' };
  }

  if (startsWithBytes(bytes, CLUSTER_ID) && cachedWebmInitSegment) {
    console.warn('[FileWhisperRecorder] WebM header missing, prepending cached init segment');
    return {
      blob: new Blob([cachedWebmInitSegment, bytes], { type }),
      headerStatus: 'repaired',
    };
  }

  return { blob: new Blob([bytes], { type }), headerStatus: 'missing' };
}

/**
 * Records full audio takes with MediaRecorder, uploads on stop to Groq Whisper
 * (whisper-large-v3) via POST /api/transcription/file.
 * No live partials — text arrives after stop.
 */
const FileWhisperRecorder = forwardRef(({
  onTranscriptionUpdate,
  onError,
  onReset,
  onStop,
  onAudioSaved,
  helperText,
  additionalHelperText,
  priority = 'normal',
  transcriptSegments = [],
  selectedRole = '',
  consultationHistoryId = null,
  recordingContext = 'initial',
}, ref) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const isRecordingRef = useRef(false);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  const [audioStream, setAudioStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const texts = useRef([...transcriptSegments]);

  const cleanupStream = useCallback(() => {
    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop());
      setAudioStream(null);
    }
  }, [audioStream]);

  const pickMimeType = () => {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
    ];
    for (const type of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  };

  const uploadAndTranscribe = useCallback(async (blob, headerStatus) => {
    const language = user?.preferences?.language || 'fr';
    const form = new FormData();
    // Fields before file so @fastify/multipart exposes them on data.fields
    if (consultationHistoryId) {
      form.append('consultationHistoryId', String(consultationHistoryId));
    }
    form.append('language', language);
    form.append('role', selectedRole || '');
    form.append('context', recordingContext === 'correction' ? 'correction' : 'initial');
    form.append('webmHeader', headerStatus || 'unspecified');
    const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
    form.append('file', blob, `recording.${ext}`);

    const result = await apiClient.postForm('/api/transcription/file', form);
    return result;
  }, [user, consultationHistoryId, selectedRole, recordingContext]);

  const stopRecording = useCallback(async () => {
    if (!isRecordingRef.current || !mediaRecorderRef.current) return;

    setIsRecording(false);
    isRecordingRef.current = false;

    const recorder = mediaRecorderRef.current;
    await new Promise((resolve) => {
      recorder.onstop = resolve;
      if (recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        resolve();
      }
    });

    mediaRecorderRef.current = null;
    const mimeType = pickMimeType() || 'audio/webm';
    const rawBlob = new Blob(chunksRef.current, { type: mimeType });
    chunksRef.current = [];
    cleanupStream();
    const prepared = mimeType.includes('mp4')
      ? { blob: rawBlob, headerStatus: 'mp4' }
      : await repairWebmHeader(rawBlob);
    const blob = prepared.blob;

    if (!blob.size) {
      onError?.(t('audioRecorder.errors.start') || 'Empty recording');
      onStop?.();
      return;
    }

    setIsTranscribing(true);
    try {
      const result = await uploadAndTranscribe(blob, prepared.headerStatus);
      const text = (result?.text || '').trim();
      if (text) {
        const updated = [...texts.current.filter((s) => s !== ''), text, ''];
        texts.current = updated;
        onTranscriptionUpdate?.([...updated]);
      }
      if (result?.audioId != null) {
        onAudioSaved?.(result.audioId);
      }
    } catch (err) {
      console.error('File transcription failed:', err);
      onError?.(err?.message || String(err) || t('audioRecorder.errors.generic'));
    } finally {
      setIsTranscribing(false);
      onStop?.();
    }
  }, [cleanupStream, uploadAndTranscribe, onTranscriptionUpdate, onAudioSaved, onError, onStop, t]);

  const startRecording = useCallback(async () => {
    if (isTranscribing) return;
    texts.current = [...transcriptSegments];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      setAudioStream(stream);
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recorder.ondataavailable = (event) => {
        if (mediaRecorderRef.current !== recorder) return;
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      isRecordingRef.current = true;
    } catch (err) {
      console.error('Failed to start file recording:', err);
      onError?.(err?.message || String(err) || t('audioRecorder.errors.permissions'));
      cleanupStream();
    }
  }, [isTranscribing, transcriptSegments, onError, cleanupStream, t]);

  const handleToggleRecording = useCallback(() => {
    if (isTranscribing) return;
    if (isRecordingRef.current) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isTranscribing, startRecording, stopRecording]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'x') {
        const isInModal = document.querySelector('[role="dialog"]') !== null;
        const isInFeedbackModal = document.querySelector('[data-feedback-modal]') !== null;
        const shouldHandle = priority === 'high' || (!isInModal && priority === 'normal');
        if (isInFeedbackModal && priority !== 'high') return;
        if (shouldHandle) {
          event.preventDefault();
          event.stopPropagation();
          handleToggleRecording();
        }
      }
    };
    const useCapture = priority === 'high';
    window.addEventListener('keydown', handleKeyPress, useCapture);
    return () => window.removeEventListener('keydown', handleKeyPress, useCapture);
  }, [handleToggleRecording, priority]);

  useEffect(() => {
    if (onReset) {
      return () => {
        if (isRecordingRef.current) {
          stopRecording();
        }
      };
    }
  }, [onReset, stopRecording]);

  useImperativeHandle(ref, () => ({
    stopRecording,
  }));

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between space-x-6 mb-4">
        <div className="flex-1">
          {isRecording && audioStream ? (
            <Visualizer
              audio={audioStream}
              autoStart={true}
              strokeColor="#c5d6f8"
              mode="continuous"
              rectWidth={3}
              slices={50}
              heightNorm={1}
            >
              {({ canvasRef }) => (
                <canvas
                  ref={canvasRef}
                  style={{ height: '80px', width: '100%' }}
                  className="rounded-lg"
                />
              )}
            </Visualizer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[80px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-500">
                {isTranscribing
                  ? t('audioRecorder.transcribing')
                  : helperText || t('audioRecorder.clickToStart')}
                {!isTranscribing && (
                  <span className="ml-2 text-xs text-gray-400">
                    {t('audioRecorder.shortcut')}
                  </span>
                )}
              </p>
              {!isRecording && !isTranscribing && additionalHelperText && (
                <p className="text-xs text-gray-400 italic mt-1 px-4 text-center">
                  {additionalHelperText}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center">
          <button
            type="button"
            onClick={handleToggleRecording}
            disabled={isTranscribing}
            className={`flex items-center justify-center w-16 h-16 rounded-full transition-all duration-200 focus:outline-none focus:ring-4 ${
              isRecording
                ? 'bg-red-300 hover:bg-red-400 focus:ring-red-200'
                : isTranscribing
                  ? 'opacity-60 cursor-not-allowed bg-gray-300'
                  : 'hover:bg-teal-400 focus:ring-teal-200'
            }`}
            style={!isRecording && !isTranscribing ? {
              backgroundColor: colors.primary.selected,
            } : undefined}
            aria-label={isRecording ? t('audioRecorder.stopRecording') : t('audioRecorder.startRecording')}
          >
            {isRecording ? (
              <StopIcon className="w-8 h-8 text-white" />
            ) : (
              <MicrophoneIcon className="w-8 h-8 text-white" />
            )}
          </button>
        </div>
      </div>

      {(isRecording || isTranscribing) && (
        <div className="flex items-center justify-center space-x-2 text-sm">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isTranscribing ? 'bg-blue-500 animate-pulse' : 'bg-red-500 animate-pulse'}`} />
            <span className={`font-medium ${isTranscribing ? 'text-blue-600' : 'text-red-600'}`}>
              {isTranscribing ? t('audioRecorder.transcribingStatus') : t('audioRecorder.recordingStatus')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

export default FileWhisperRecorder;
